import { AfterContentInit, Component, ErrorHandler, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, afterNextRender, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, metadata, required, REQUIRED, SchemaPath, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatStepper } from '@angular/material/stepper'

import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../_jaettu-angular/service/maa.service'
import { WindowSizeService } from '../_jaettu-angular/service/window.service'

import { LemonaidValidators } from '../_angular/_validator/LemonaidValidators'

import {
  CustomerBeneficiary,
  CustomerHallituksenJasen,
  CustomerHallituksenJasenRole,
  CustomerOsakas,
  KnowYourCustomer,
  LemonaidAsiakkaanKuukausihinta,
  LemonaidAsiakkaanMuuJatkuvaVeloitus,
  LemonaidAsiakkaanSopimuskausi,
} from '@lemon/core-firebase/model/kayttaja'
import {
  KnowYourCustomerSaveRequest,
  KnowYourCustomerSaveResponse,
  PalvelukuvauksenPdfRequest,
  PalvelukuvauksenPdfResponse,
  SopimuksenHyvaksynnanRequest,
  SopimuksenHyvaksynnanResponse,
  SOPIMUS_LIITE_NIMET,
  SOPIMUS_LIITETYYPIT,
  SopimusLiiteAsiakkaanHakuPyynto,
  SopimusLiiteAsiakkaanHakuVastaus,
  SopimusLiitetyyppi,
  SopimusPdfRequest,
  SopimusPdfResponse,
} from '@lemon/core-firebase/model/sopimus'
import { tuotenimikkeet } from '@lemon/core-firebase/model/tuotenimikkeet'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { ErrorResponse, LocalDate, NumberDate } from '@lemon/core/model/common'
import { CurrencyService } from '@lemon/core/service/currency.service'

import { TallennaUserKycRequest, TallennaUserKycResponse } from '@lemon/core-firebase/model/tunnistaminen'
import { AllowedDialogsService } from 'app/_angular/service/allowed-dialogs.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { TunnistautuminenService } from 'app/_angular/service/tunnistautuminen.service'
import { Observable, Subject } from 'rxjs'
import { map, takeUntil, takeWhile } from 'rxjs/operators'
import { PepTekstiSharedDialog } from './pep-teksti-shared.dialog'

import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { StringService } from '@lemon/core/service/string.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { TuntemistiedotDialogService } from 'app/_angular/service/tuntemistiedot-dialog.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { TiedostojenLataamisService } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'

export interface TervetuloaSopimusV2DialogData {
  naytaSopimus: boolean
  naytaTunnistaminen: boolean
  naytaTuntemistiedot: boolean
  mobiili: boolean
  hinta: LemonaidAsiakkaanKuukausihinta
  kayttajaJoTunnistettu: boolean
  pepMissing: boolean
  sopimuskausi: LemonaidAsiakkaanSopimuskausi
  muutJatkuvatVeloitukset?: LemonaidAsiakkaanMuuJatkuvaVeloitus[]
}

interface SopimusLomake {
  hyvaksy: boolean
}

interface ValtuutusLomake {
  hetu: string
}

interface TuntemistiedotHenkiloBaseLomake {
  nimi: string
  hetu: string
  kansallisuus: string | null
  pep: boolean | null
  noFinnishId: boolean | null
  birthDate: Date | null
}

interface TuntemistiedotOsakasLomake extends TuntemistiedotHenkiloBaseLomake {
  osakkeita: number | null
  onkoYritys: boolean | null
}

interface TuntemistiedotHallitusLomake extends TuntemistiedotHenkiloBaseLomake {
  rooli: CustomerHallituksenJasenRole | null
}

interface TuntemistiedotLomake {
  osakkaat: TuntemistiedotOsakasLomake[]
  hallitus: TuntemistiedotHallitusLomake[]
  muut: TuntemistiedotHenkiloBaseLomake[]
}

interface TunnistautujaLomake {
  nimi: string
  kansallisuus: string | null
  pep: boolean | null
}

// Reaktiivisen lomakkeen required hyväksyy arvon false, Signal Formsin required ei
function vaadiTotuusarvo(polku: SchemaPath<boolean | null>) {
  metadata(polku, REQUIRED, () => true)
  validoiReaktiivisesti(polku, Validators.required)
}

function vaadiVahintaanYksi(polku: SchemaPath<unknown[]>) {
  validate(polku, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
}

interface VeloituksetViewable {
  nimi: string
  hinta: number
  frekvenssi: string
  lisatiedot?: string
}

@Component({
  templateUrl: './tervetuloa-sopimus-v2.dialog.html',
  styleUrls: ['./tervetuloa-sopimus-v2.dialog.css'],
  // Valintanappiryhmän virhetyyli (styles.css) nojaa reaktiivisten lomakkeiden ng-invalid- ja ng-touched-luokkiin
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TervetuloaSopimusV2Dialog implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild('horizStepper', { read: MatStepper }) horizStepper: MatStepper
  @ViewChild('verticStepper', { read: MatStepper }) verticStepper: MatStepper

  private _ngUnsubscribe = new Subject<void>()

  sopimusLomake = form(signal<SopimusLomake>({ hyvaksy: false }), polku => {
    validoiReaktiivisesti(polku.hyvaksy, Validators.requiredTrue)
  })
  tunnistautujaLomake = form(signal<TunnistautujaLomake>({ nimi: '', kansallisuus: null, pep: null }), polku => {
    disabled(polku.nimi)
    required(polku.nimi)
    required(polku.kansallisuus)
    vaadiTotuusarvo(polku.pep)
  })
  valtuutusLomake = form(signal<ValtuutusLomake>({ hetu: '' }), polku => {
    required(polku.hetu)
    validoiReaktiivisesti(polku.hetu, LemonaidValidators.finnishPersonalIdValidator)
  })
  tuntemistiedotLomake = form(signal<TuntemistiedotLomake>({ osakkaat: [], hallitus: [], muut: [] }), polku => {
    vaadiVahintaanYksi(polku.osakkaat)
    applyEach(polku.osakkaat, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishHetuYtunnusOrVatNumberValidator)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.osakkeita)
      required(rivi.kansallisuus)
      vaadiTotuusarvo(rivi.pep)
      vaadiTotuusarvo(rivi.onkoYritys)
      disabled(rivi.onkoYritys, ({ valueOf }) => !valueOf(rivi.noFinnishId))
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId) || !!valueOf(rivi.onkoYritys))
    })
    vaadiVahintaanYksi(polku.hallitus)
    applyEach(polku.hallitus, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishHetuYtunnusOrVatNumberValidator)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.kansallisuus)
      vaadiTotuusarvo(rivi.pep)
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId))
      required(rivi.rooli)
    })
    applyEach(polku.muut, rivi => {
      required(rivi.nimi)
      required(rivi.hetu)
      validoiReaktiivisesti(rivi.hetu, LemonaidValidators.finnishPersonalIdValidator, { when: ({ valueOf }) => !valueOf(rivi.noFinnishId) })
      required(rivi.kansallisuus)
      vaadiTotuusarvo(rivi.pep)
      required(rivi.birthDate)
      disabled(rivi.birthDate, ({ valueOf }) => !valueOf(rivi.noFinnishId))
    })
  })
  readonly cellphone = signal(true)
  naytaSopimus: boolean
  naytaTunnistaminen: boolean
  naytaTuntemistiedot: boolean
  readonly commonError = signal<string | null>(null)
  readonly kieli = signal<TuettuKieli>('fi')

  readonly liitetyypit = SOPIMUS_LIITETYYPIT
  readonly liiteNimet = SOPIMUS_LIITE_NIMET

  sopimuskaudenVersio: LemonaidAsiakkaanSopimuskausi = this._data.sopimuskausi

  private readonly _injector = inject(Injector)
  private _osakkaidenLopetukset: Subject<void>[] = []
  private _hallituksenLopetukset: Subject<void>[] = []
  private _edunsaajienLopetukset: Subject<void>[] = []

  private _tunnistamistiedot: KnowYourCustomer = {
    asiakasAvain: null,
    avain: null,
    edunsaajat: [],
    hallitus: [],
    luoja: null,
    luotu: null,
    osakkaat: [],
    paivitetty: null,
    paivittaja: null,
    poistettu: false,
  }

  ensimmainenLaskutettavaKuukausi: string
  // sopimusTekstiObservable: Observable<string>
  hintaPalvelunAlkaessaObservable: Observable<string>
  // naytaSopimusObservable: Observable<boolean>
  // naytaHetuObservable: Observable<boolean>
  // naytaTuntemistiedotObservable: Observable<boolean>
  hallitusRoolitObservable: Observable<{ avain: Pick<CustomerHallituksenJasen, 'rooli'>; nimi: string }[]>
  readonly tunnistautujanTiedotAnnettu = signal(false)
  strongAuthResult: 'start' | 'success' | 'error' | 'error-cancel' = 'start'

  kansallisuudetObservable: Observable<Maa[]>
  readonly viewableMuutJatkuvatVeloitukset = signal<VeloituksetViewable[]>(undefined)

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: TervetuloaSopimusV2DialogData,
    private _dialogRef: MatDialogRef<TervetuloaSopimusV2Dialog>,
    private _dialog: MatDialog,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _currencyService: CurrencyService,
    private _windowSizeService: WindowSizeService,
    private _allowedDialogsService: AllowedDialogsService,
    private _maaService: MaaService,
    private _tunnistautuminenService: TunnistautuminenService,
    private _kayttajaService: KayttajaService,
    private _dateService: DateService,
    private _tuntemistiedotDialogService: TuntemistiedotDialogService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _fileSaverService: FileSaverService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _stringService: StringService,
    private _asiakasJaettuService: AsiakasJaettuLemonaidService,
  ) {
    this.naytaSopimus = this._data.naytaSopimus
    this.naytaTunnistaminen = this._data.naytaTunnistaminen
    this.naytaTuntemistiedot = this._data.naytaTuntemistiedot

    this.cellphone.set(window.innerWidth < 670 || window.innerHeight < 670)

    this._windowSizeService.sizeObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this.cellphone.set(window.innerWidth < 670 || window.innerHeight < 670)
    })

    const hintaVoimassaAlkaen: NumberDate = this._data.hinta?.voimassaAlkaen
    const hintaVoimassaLocalDate: LocalDate = typeof hintaVoimassaAlkaen === 'number' ? this._dateService.numberToLocalDate(hintaVoimassaAlkaen) : hintaVoimassaAlkaen // Extra check to handle AsiakkaanHinta objects where this._data.hinta.voimassaAlkaen is not migrated yet (LocalDate -> NumberDate)
    this.ensimmainenLaskutettavaKuukausi = hintaVoimassaAlkaen ? hintaVoimassaLocalDate.month + '/' + hintaVoimassaLocalDate.year : ''

    this.hintaPalvelunAlkaessaObservable = this._translationService.currentLanguageObservable.pipe(map(kieli => this._currencyService.formatoiRaha(this._data.hinta?.hinta, 'EUR', kieli)))

    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(k => {
      this.kieli.set(k || 'fi')
    })

    this.tunnistautujanTiedotAnnettu.set(!this._data.pepMissing)
  }

  ngOnInit(): void {
    // this._kayttajaService.nykyinenAsiakasObservable.pipe(
    //   takeWhile(asiakas => !asiakas, true)
    // ).subscribe(asiakas => {
    //   if (asiakas.ytunnus) {
    //     this._ytunnus = asiakas.ytunnus.trim()
    //   }
    // })

    if (this.naytaTuntemistiedot) {
      // Save KYC-seen log if the entire pop-up is shown
      this._kayttajaService
        .getKayttajanTiedot()
        .then(kayttajanTiedot => {
          if (kayttajanTiedot?.asiakasAvain) {
            this._tuntemistiedotDialogService.savePopupSeenLog(kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid)
          }
        })
        .catch(err => {
          console.log(err)
        })
    }

    this._dialogRef
      .beforeClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._allowedDialogsService.allowAll()
      })

    this.kansallisuudetObservable = this._maaService.kaikkiMaatObservable.pipe(
      map(kaikkiMaat => {
        const maatCopy = [...kaikkiMaat]
        // Make selecting a bit easier
        const suomi = kaikkiMaat.find(maa => maa.koodi === 'FIN')
        const ruotsi = kaikkiMaat.find(maa => maa.koodi === 'SWE')
        maatCopy.unshift({ nimi: '---', koodi: null })
        maatCopy.unshift(ruotsi)
        maatCopy.unshift(suomi)
        return maatCopy
      }),
    )

    this.hallitusRoolitObservable = this._translationService.currentLanguageObservable.pipe(
      map(kieli => {
        const kaytettyKieli = kieli === 'en' ? 'en' : 'fi'
        const roolit = [
          { avain: 'puheenjohtaja', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-puheenjohtaja', kaytettyKieli) },
          { avain: 'jasen', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-jasen', kaytettyKieli) },
          { avain: 'varajasen', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.hallituksen-varajasen', kaytettyKieli) },
          { avain: 'toimitusjohtaja', nimi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.kyc.toimitusjohtaja', kaytettyKieli) },
        ] as { avain: Pick<CustomerHallituksenJasen, 'rooli'>; nimi: string }[]

        return roolit
      }),
    )

    this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kieli => {
      this.viewableMuutJatkuvatVeloitukset.set(
        this._data.muutJatkuvatVeloitukset?.map(v => {
          const tuotenimike = tuotenimikkeet.find(t => t.avain === v.tuote)
          return {
            nimi: kieli === 'en' ? tuotenimike.engNimi : tuotenimike.nimi,
            hinta: v.hinta,
            frekvenssi: this._translationService.lokalisoiKielella('lemonaid-sopimus-dialog.' + v.frekvenssi, kieli),
            lisatiedot: v.lisatiedot,
          }
        }),
      )
    })

    this._kayttajaService.kayttajaObservable.pipe(takeWhile(kayttaja => !kayttaja, true)).subscribe(kayttaja => {
      asetaArvo(this.tunnistautujaLomake.nimi, kayttaja ? kayttaja.etunimi + ' ' + kayttaja.sukunimi : '')
    })

    this.lisaaOsakas()
    this.lisaaHallituksenJasen()
  }

  private _getActiveStepper(): MatStepper | null {
    if (this.horizStepper) {
      return this.horizStepper
    }
    if (this.verticStepper) {
      return this.verticStepper
    }
    return null
  }

  private _executeOnStepper(toExecute: (stepper: MatStepper) => void) {
    const stepper = this._getActiveStepper()
    if (stepper) {
      toExecute(stepper)
    } else {
      afterNextRender(() => this._executeOnStepper(toExecute), { injector: this._injector })
    }
  }

  ngAfterContentInit() {
    const tunnistautuminenState = this._tunnistautuminenService.getTunnistautuminenState()
    if (tunnistautuminenState) {
      // User has returned from strong auth
      this.strongAuthResult = tunnistautuminenState
      this._tunnistautuminenService.endClientSideAuthenticationSession()
      // Switch tab to tunnistaminen (index 1)
      this._executeOnStepper(stepper => {
        setTimeout(() => {
          stepper.selectedIndex = 1
        }, 500)
      })
    } else if (this._data.kayttajaJoTunnistettu && this._data.pepMissing) {
      // User has already completed strong auth, but is missing PEP data
      this.strongAuthResult = 'success'
      // Switch tab to tunnistaminen (index 1)
      this._executeOnStepper(stepper => {
        setTimeout(() => {
          stepper.selectedIndex = 1
        }, 500)
      })
    }
  }

  get osakasRivit() {
    return this.tuntemistiedotLomake.osakkaat
  }

  get hallitusRivit() {
    return this.tuntemistiedotLomake.hallitus
  }

  get edunsaajaRivit() {
    return this.tuntemistiedotLomake.muut
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private _rivinMuutokset<T>(kentta: FieldTree<T>, lopetus: Subject<void>) {
    return arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe), takeUntil(lopetus))
  }

  poistaOsakas(index: number) {
    this._osakkaidenLopetukset.splice(index, 1)[0]?.next()
    asetaArvo(
      this.osakasRivit,
      this.osakasRivit()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tunnistamistiedot.osakkaat.splice(index, 1)
  }

  lisaaOsakas() {
    const osakas: CustomerOsakas = {
      hetuTaiYtunnus: '',
      nimi: '',
      osakkeita: null,
      kansallisuus: null,
      pep: null,
    }

    const rivit = this.osakasRivit().value()
    asetaArvo(
      this.osakasRivit,
      [
        ...rivit,
        {
          nimi: osakas.nimi,
          hetu: osakas.hetuTaiYtunnus,
          osakkeita: osakas.osakkeita,
          kansallisuus: osakas.kansallisuus,
          pep: osakas.pep,
          noFinnishId: null,
          onkoYritys: null,
          birthDate: osakas.birthDate ? this._dateService.localDateToDate(osakas.birthDate) : null,
        },
      ],
      { ilmoita: false },
    )
    const osakasRivi = this.osakasRivit[rivit.length]
    const lopetus = new Subject<void>()
    this._osakkaidenLopetukset.push(lopetus)

    this._rivinMuutokset(osakasRivi.nimi, lopetus).subscribe(value => {
      osakas.nimi = value || null
    })
    this._rivinMuutokset(osakasRivi.hetu, lopetus).subscribe(value => {
      osakas.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
    })
    this._rivinMuutokset(osakasRivi.osakkeita, lopetus).subscribe(value => {
      osakas.osakkeita = value || null
    })
    this._rivinMuutokset(osakasRivi.kansallisuus, lopetus).subscribe(value => {
      osakas.kansallisuus = value || null
    })
    this._rivinMuutokset(osakasRivi.pep, lopetus).subscribe(value => {
      osakas.pep = value ?? null
    })
    this._rivinMuutokset(osakasRivi.noFinnishId, lopetus).subscribe(value => {
      osakas.noFinnishId = value ?? false
    })

    this._rivinMuutokset(osakasRivi.birthDate, lopetus).subscribe(value => {
      if (value && osakasRivi.birthDate().valid()) {
        osakas.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    this._rivinMuutokset(osakasRivi.onkoYritys, lopetus).subscribe(value => {
      osakas.onkoYritys = value ?? null
    })

    asetaArvo(osakasRivi.noFinnishId, osakas.noFinnishId ?? null)

    this._tunnistamistiedot.osakkaat.push(osakas)
  }

  poistaHallituksenJasen(index: number) {
    this._hallituksenLopetukset.splice(index, 1)[0]?.next()
    asetaArvo(
      this.hallitusRivit,
      this.hallitusRivit()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tunnistamistiedot.hallitus.splice(index, 1)
  }

  lisaaHallituksenJasen() {
    const jasen: CustomerHallituksenJasen = {
      hetuTaiYtunnus: '',
      nimi: '',
      kansallisuus: null,
      pep: null,
      rooli: null,
    }

    const rivit = this.hallitusRivit().value()
    asetaArvo(
      this.hallitusRivit,
      [
        ...rivit,
        {
          nimi: jasen.nimi,
          hetu: jasen.hetuTaiYtunnus,
          kansallisuus: jasen.kansallisuus,
          pep: jasen.pep,
          noFinnishId: null,
          birthDate: jasen.birthDate ? this._dateService.localDateToDate(jasen.birthDate) : null,
          rooli: jasen.rooli,
        },
      ],
      { ilmoita: false },
    )
    const jasenRivi = this.hallitusRivit[rivit.length]
    const lopetus = new Subject<void>()
    this._hallituksenLopetukset.push(lopetus)

    this._rivinMuutokset(jasenRivi.nimi, lopetus).subscribe(value => {
      jasen.nimi = value || null
    })
    this._rivinMuutokset(jasenRivi.hetu, lopetus).subscribe(value => {
      jasen.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
    })
    this._rivinMuutokset(jasenRivi.kansallisuus, lopetus).subscribe(value => {
      jasen.kansallisuus = value || null
    })
    this._rivinMuutokset(jasenRivi.pep, lopetus).subscribe(value => {
      jasen.pep = value ?? null
    })
    this._rivinMuutokset(jasenRivi.noFinnishId, lopetus).subscribe(value => {
      jasen.noFinnishId = value ?? false
    })
    this._rivinMuutokset(jasenRivi.birthDate, lopetus).subscribe(value => {
      if (value && jasenRivi.birthDate().valid()) {
        jasen.birthDate = this._dateService.dateToLocalDate(value)
      }
    })
    this._rivinMuutokset(jasenRivi.rooli, lopetus).subscribe(value => {
      jasen.rooli = value || null
    })

    asetaArvo(jasenRivi.noFinnishId, jasen.noFinnishId ?? null)
    this._tunnistamistiedot.hallitus.push(jasen)
  }

  poistaEdunsaaja(index: number) {
    this._edunsaajienLopetukset.splice(index, 1)[0]?.next()
    asetaArvo(
      this.edunsaajaRivit,
      this.edunsaajaRivit()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tunnistamistiedot.edunsaajat.splice(index, 1)
  }

  lisaaEdunsaaja() {
    const saaja: CustomerBeneficiary = {
      hetuTaiYtunnus: '',
      nimi: '',
      kansallisuus: null,
      pep: null,
    }

    const rivit = this.edunsaajaRivit().value()
    asetaArvo(
      this.edunsaajaRivit,
      [
        ...rivit,
        {
          nimi: saaja.nimi,
          hetu: saaja.hetuTaiYtunnus,
          kansallisuus: saaja.kansallisuus,
          pep: saaja.pep,
          noFinnishId: null,
          birthDate: saaja.birthDate ? this._dateService.localDateToDate(saaja.birthDate) : null,
        },
      ],
      { ilmoita: false },
    )
    const saajaRivi = this.edunsaajaRivit[rivit.length]
    const lopetus = new Subject<void>()
    this._edunsaajienLopetukset.push(lopetus)

    this._rivinMuutokset(saajaRivi.nimi, lopetus).subscribe(value => {
      saaja.nimi = value || null
    })
    this._rivinMuutokset(saajaRivi.hetu, lopetus).subscribe(value => {
      saaja.hetuTaiYtunnus = this._stringService.normalizeDash(value) || null
    })
    this._rivinMuutokset(saajaRivi.kansallisuus, lopetus).subscribe(value => {
      saaja.kansallisuus = value || null
    })
    this._rivinMuutokset(saajaRivi.pep, lopetus).subscribe(value => {
      saaja.pep = value ?? null
    })
    this._rivinMuutokset(saajaRivi.noFinnishId, lopetus).subscribe(value => {
      saaja.noFinnishId = value ?? false
    })
    this._rivinMuutokset(saajaRivi.birthDate, lopetus).subscribe(value => {
      if (value && saajaRivi.birthDate().valid()) {
        saaja.birthDate = this._dateService.dateToLocalDate(value)
      }
    })

    asetaArvo(saajaRivi.noFinnishId, saaja.noFinnishId ?? null)

    this._tunnistamistiedot.edunsaajat.push(saaja)
  }

  get hyvaksy() {
    return this.sopimusLomake.hyvaksy
  }

  get hetu() {
    return this.valtuutusLomake.hetu
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async downloadSopimusPreview() {
    this._ladataanService.aloitaLataaminen()

    const req: SopimusPdfRequest = {
      versio: 'uusin',
      kieli: this._translationService.nykyinenKieli || 'fi',
    }
    return this._firebaseLemonaid
      .functionsCall<SopimusPdfRequest, SopimusPdfResponse>('sopimusAnnaPdfPreview', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          this._downloadSopimusPdf(response.base64String)
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => {
        this._ladataanService.lopetaLataaminen()
      })
  }

  async downloadLiite(liitetyyppi: SopimusLiitetyyppi) {
    this._ladataanService.aloitaLataaminen()
    const req: SopimusLiiteAsiakkaanHakuPyynto = { liitetyyppi, kieli: this._translationService.nykyinenKieli || 'fi' }
    return this._firebaseLemonaid
      .functionsCall<SopimusLiiteAsiakkaanHakuPyynto, SopimusLiiteAsiakkaanHakuVastaus>('sopimusAnnaLiite', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(response.base64String)
          const nimi = this._translationService.lokalisoi(this.liiteNimet[liitetyyppi], this._translationService.nykyinenKieli || 'fi')
          this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), nimi + '.pdf')
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  private _downloadSopimusPdf(base64File: string) {
    const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(base64File)
    const fileName = 'Tilitoimisto Lemon Tree Oy Toimeksiantosopimus.pdf'
    this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
  }

  async downloadPalvelukuvaus() {
    this._ladataanService.aloitaLataaminen()
    const req: PalvelukuvauksenPdfRequest = {
      kieli: this._translationService.nykyinenKieli || 'fi',
    }
    return this._firebaseLemonaid
      .functionsCall<PalvelukuvauksenPdfRequest, PalvelukuvauksenPdfResponse>('sopimusAnnaPalvelukuvausPdf', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          this._downloadPalvelukuvausPdf(response.base64String)
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  private _downloadPalvelukuvausPdf(base64File: string) {
    const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(base64File)
    const fileName = 'Tilitoimisto Lemon Tree Oy Palvelukuvaus.pdf'
    this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
  }

  async tallennaTunnistautujaLisatiedot() {
    if (!this.tunnistautujaLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.tunnistautujaLomake)
      return
    }

    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    const req: TallennaUserKycRequest = {
      kansallisuus: this.tunnistautujaLomake.kansallisuus().value(),
      pep: this.tunnistautujaLomake.pep().value(),
    }
    return this._firebaseLemonaid
      .functionsCall<TallennaUserKycRequest, TallennaUserKycResponse>('tunnistautuminenTallennaUserKyc', req)
      .then(vastaus => {
        if (vastaus?.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(vastaus.e)
        } else {
          this.tunnistautujanTiedotAnnettu.set(true)
        }
      })
      .catch(err => {
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
        this._errorHandler.handleError(err)
      })
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  tallennaKyc() {
    if (!this.tuntemistiedotLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.tuntemistiedotLomake)
      return
    }

    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    // Remove unnecessary data (birthDate & onkoYritys fields are disabled when the osakas has a Finnish hetu/y-tunnus)
    for (const osakas of this._tunnistamistiedot.osakkaat) {
      if (!osakas.noFinnishId) {
        osakas.birthDate = null
        osakas.onkoYritys = null
      }
    }

    // Call CF
    const tallennusData: KnowYourCustomerSaveRequest = {
      kyc: this._tunnistamistiedot,
    }
    return this._firebaseLemonaid
      .functionsCall<KnowYourCustomerSaveRequest, KnowYourCustomerSaveResponse>('knowYourCustomerTallennus', tallennusData)
      .then(response => {
        // Stop loading indicator
        this._ladataanService.lopetaLataaminen()

        // If no response, or response contains an error
        if (!response || response.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          // All is good, let's advance
          this._executeOnStepper(stepper => stepper.next())
        }
      })
      .catch(error => {
        this._ladataanService.lopetaLataaminen()
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
        this._errorHandler.handleError(error)
      })
  }

  tallennaHyvaksynta() {
    if (!this.sopimusLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.sopimusLomake)
      return
    }

    // Show loading indicator
    this._ladataanService.aloitaLataaminen()

    // Call CF
    const tallennusData: SopimuksenHyvaksynnanRequest = { asiakkaanSopimusversionAvain: this._data.sopimuskausi.sopimusversioAvain, kieli: this._translationService.nykyinenKieli }
    return this._firebaseLemonaid
      .functionsCall<SopimuksenHyvaksynnanRequest, SopimuksenHyvaksynnanResponse>('sopimusHyvaksynnanTallennus', tallennusData)
      .then(response => {
        // If no response, or response contains an error
        if (!response || response.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          // All is good, let's advance
          this._executeOnStepper(stepper => stepper.next())
        }
      })
      .catch(error => {
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
        this._errorHandler.handleError(error)
      })
      .finally(() => {
        this._ladataanService.lopetaLataaminen()
      })
  }

  saveCurrentStep(event: MouseEvent) {
    const activeStepper: MatStepper = this._getActiveStepper()
    const activeStepperControl = activeStepper?.selected?.stepControl as FieldTree<unknown>
    activeStepperControl?.().markAsTouched()
    if (activeStepperControl?.().errors().length) {
      return
    }
    const currentStepLabel = activeStepper?.selected?.label

    if (currentStepLabel === 'sopimus') {
      this.stopClick(event)
      return this.tallennaHyvaksynta()
    }
    if (currentStepLabel === 'tunnistaminen') {
      this.stopClick(event)
      return
    }
    if (currentStepLabel === 'tuntemistiedot') {
      this.stopClick(event)
      return this.tallennaKyc()
    }
  }

  openPepText() {
    this._dialog.open(PepTekstiSharedDialog)
  }

  stopClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  valmis() {
    this._ladataanService.aloitaLataaminen()
    return this._firebaseLemonaid
      .functionsCall<string, ErrorResponse>('tervetuloaDialogiNaytettyPaivitys', '')
      .then(response => {
        // Stop loading indicator
        this._ladataanService.lopetaLataaminen()

        // If no response, or response contains an error
        if (!response || response.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          // All is good, close dialog
          this._dialogRef.close()
        }
      })
      .catch(error => {
        this._ladataanService.lopetaLataaminen()
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
        this._errorHandler.handleError(error)
      })
  }

  // isCompanysOwnYtunnusValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  //   if (!!this._ytunnus &&
  //     this._ytunnus === control.value?.trim()
  //   ) {
  //     return { ownytunnus: true }
  //   }
  //   return null
  // }
}
